import styles from './template.css';
import template from './template';
import {i18nMixin} from '@aofl/i18n-mixin';
import {AoflElement, customElement, property} from '@aofl/element';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {storeInstance} from '@aofl/store/dist/legacy';
import {groupTypes, sdoNs, userTypes, modals} from '../constants-enumerate';
import translations from '../i18n';
import {ApiService} from '@modules/api-service';
import {SimpleModal} from '@modules/simple-modal';

/**
 * @summary ClassSelection
 * @extends {AoflElement}
 */
@customElement('class-selection')
class ClassSelection extends i18nMixin(mapStatePropertiesMixin(AoflElement)) {
  /**
   * @readonly
   */
  static is = 'class-selection';

  /**
   * Creates an instance of ClassSelection.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;
  }

  @property({type: Object})
  groups = {};

  @property({type: Object})
  data = {};

  @property({type: Array})
  groupsToHide = [];

  @property({type: Array})
  selectedGroupIds = [];

  @property({type: Object})
  classSearchResponse = {};

  @property({type: Number, mapState: `${sdoNs.SESSION}.group_info.id`, store: storeInstance})
  rootGroupId = 0;

  @property({type: Number, mapState: `${sdoNs.SESSION}.$classLevel`, store: storeInstance})
  classLevel = 0;

  @property({type: Object})
  search = void 0;

  @property({type: Number})
  classSearchLimit = 10;

  @property({type: String, mapState: `${sdoNs.SESSION}.type`, store: storeInstance})
  currentUserType = '';

  @property({type: String, attribute: false, mapState: `${sdoNs.SESSION}.group_info.name`, store: storeInstance})
  groupName = '';

  @property({type: String, attribute: false, mapState: `${sdoNs.SESSION}.group_info.path`, store: storeInstance})
  rolePath = '';

  @property({type: Boolean})
  isTeacher = false;

  connectedCallback() {
    super.connectedCallback();
    this.isTeacher = (this.currentUserType === userTypes.TEACHER);

    const classesBySchoolItems = Object.values(this.data.classesBySchool);
    if (classesBySchoolItems.length > 0) {
      classesBySchoolItems.forEach((element) => {
        this.groupsToHide = this.groupsToHide.concat(element.map((e) => e.id));
      });
    }
  }

  triggerSearch(e, groupInfo) {
    this.search = e.detail;
    this.getSiblingClasses(groupInfo);
  }

  async getSiblingClasses(classInfo) {
    const search = (this.search?.query) ?
      this.search :
      void 0;
    this.classSearchResponse = await ApiService.groupSearch(
      search,
      {
        path: classInfo.path,
        level: classInfo.path.split('.').length,
        type: groupTypes.CLASSROOM
      },
      {
        fields: ['id', 'name'],
        direction: 'asc',
        limit: this.classSearchLimit,
      },
      true
    );
  }

  async addClass(classInfo) {
    this.groupsToHide.push(classInfo.id);
    let path = classInfo.path.replace(this.rolePath, '').split('.');
    path.pop();
    path = path.map((id) => parseInt(id, 10));
    const groupPathInfo = await ApiService.groupGet(path, ['id', 'name', 'path']);
    groupPathInfo.results[classInfo.id] = classInfo;
    this.selectedGroupIds = [];

    this.dispatchEvent(new CustomEvent('data-updated', {
      composed: true,
      bubbles: true,
      detail: {
        action: 'add',
        selectedGroups: Object.values(groupPathInfo.results),
        class: classInfo
      }
    }));
  }

  onGroupSelect(e) {
    const state = storeInstance.getState();
    const lastInfo = e.target.selectedGroupsInfo[e.detail.groupInfo.id];
    const fullPath = e.detail.groupInfo.path + e.detail.groupInfo.id + '.';
    const groupInfo = state[sdoNs.SESSION].group_info;
    const pathArr = fullPath.split('.');
    pathArr.pop();
    this.selectedGroupIds = pathArr.slice(groupInfo.path.split('.').length);
    this.selectedGroupsInfo = e.target.selectedGroupsInfo;
    if (lastInfo.path.split('.').length === this.classLevel) {
      this.groupsToHide.push(e.detail.groupInfo.id);
      this.selectedGroupIds = [];
      const classInfo = {
        ...lastInfo,
        full_path: lastInfo.path + lastInfo.id + '.'
      };
      this.dispatchEvent(new CustomEvent('data-updated', {
        composed: true,
        bubbles: true,
        detail: {
          action: 'add',
          selectedGroups: Object.values(e.target.selectedGroupsInfo),
          class: classInfo
        }
      }));
    }
  }

  removeClasses(classes) {
    if (this.data.addNewSchool && classes.length === 0) {
      this.dispatchEvent(new CustomEvent('data-updated', {
        composed: true,
        bubbles: true,
        detail: {
          action: 'removeEmpty'
        }
      }));
    } else {
      classes.map((classInfo, index) => {
        classInfo.index -= index;
        this.removeClass(classInfo);
      });
    }
  }

  removeClass(classInfo) {
    const index = this.groupsToHide.indexOf(classInfo.id);
    if (index > -1) this.groupsToHide.splice(index, 1);

    this.dispatchEvent(new CustomEvent('data-updated', {
      composed: true,
      bubbles: true,
      detail: {
        action: 'remove',
        classInfo
      }
    }));
  }

  classClicked(e) {
    SimpleModal.show(modals.CLASS_EDIT, {id: parseInt(e.classInfo.id, 10)}, e);
  }

  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default ClassSelection;
